/**
 * What a household used, and which of them used it.
 *
 * "From member, should be able to generate billing or usage reports per
 * account and their dependents."
 *
 * The dependent breakout is the point. A statement is a total and a list
 * of lines; the question a member rings up with is "what is my daughter
 * spending at the pool", and answering it from a statement means reading
 * two hundred lines and recognizing names.
 *
 * ---------------------------------------------------------------------
 * WHAT IS NOT ON IT
 *
 * Payments. This is what the household USED, not what it settled — a
 * member who paid four thousand in March did not consume four thousand
 * of golf in March, and netting the two produces a figure that means
 * nothing. The statement is where money in and money out belong
 * together, and it already exists.
 *
 * ---------------------------------------------------------------------
 * THE PARTS ADD UP, INCLUDING THE AWKWARD ONES
 *
 * Two rows on this screen exist so that people + unattributed = the
 * departments total, exactly:
 *
 *   Somebody who is NOT on the membership but signed here. That is a
 *   guest arrangement — the host signs, the fee bills to the guest's
 *   own account — and hiding it was the first version of this, which
 *   produced a report whose people did not add up to its departments.
 *
 *   Charges nobody signed for: dues, an assessment, a credit typed in
 *   by the office. Named rather than dropped.
 *
 * A report whose parts do not sum to its total is a report somebody
 * checks once and never trusts again.
 */

import { useState } from 'react';
import { getToken } from '../lib/api.ts';
import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date, money } from '../lib/format.ts';
import { BarList, Card, Empty, ErrorBanner, Loading } from './ui.tsx';
import { clubToday, shiftDate } from '../lib/sheet.ts';

interface Department {
  department: string;
  outlet: string;
  transactions: number;
  net_minor: string;
  tax_minor: string;
  gratuity_minor: string;
  total_minor: string;
}

interface Person {
  person_id: string;
  name: string;
  role: string;
  on_the_membership: boolean;
  transactions: number;
  total_minor: string;
  last_used_on: string | null;
}

interface Usage {
  account: { id: string; account_number: string; display_name: string };
  from: string;
  to: string;
  by_department: Department[];
  by_person: Person[];
  unattributed: { total_minor: string; transactions: number };
  total_minor: number;
}

export function UsageReport({ accountId }: { accountId: string }) {
  const { currency, club } = useSession();
  const zone = club?.timezone;

  /**
   * Blank means the club's own fiscal year to date, which the server
   * works out — a member asking what they have spent means since the
   * dues year began, not "the last ninety days".
   */
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();

  const q = useApi<Usage>(
    `/v1/member-accounts/${accountId}/usage${query ? `?${query}` : ''}`,
    [query],
  );

  /**
   * The PDF and the CSV, fetched rather than linked.
   *
   * The routes are under /v1 and authenticated, and an <a href> carries
   * no Authorization header — the same constraint the photographs hit.
   * Fetched with the token and handed to the browser as a blob.
   */
  const download = async (format: 'pdf' | 'csv') => {
    const url =
      `/v1/member-accounts/${accountId}/usage?format=${format}` +
      (query ? `&${query}` : '');
    const res = await fetch(url, {
      headers: { authorization: `Bearer ${getToken() ?? ''}` },
    });
    if (!res.ok) return;

    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    // The server already named the file in content-disposition, and a
    // blob download ignores it — so the same name is rebuilt here.
    a.download = `usage-${q.data?.account.account_number ?? accountId}-${
      q.data?.from ?? ''
    }-to-${q.data?.to ?? ''}.${format}`;
    a.click();
    // Revoked on the next tick, not immediately: Safari has not started
    // the download by the time click() returns.
    setTimeout(() => URL.revokeObjectURL(href), 2000);
  };

  const d = q.data;
  const spent = Number(d?.total_minor ?? 0);

  return (
    <Card
      title="Usage"
      actions={
        <div className="actions">
          <button className="secondary small" onClick={() => void download('csv')}>
            CSV
          </button>
          <button className="secondary small" onClick={() => void download('pdf')}>
            PDF
          </button>
        </div>
      }
    >
      <ErrorBanner error={q.error} />

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <div className="spacer" />
        <button
          className="secondary small"
          onClick={() => {
            setFrom(shiftDate(clubToday(zone), -30));
            setTo(clubToday(zone));
          }}
        >
          Last 30 days
        </button>
        <button
          className="secondary small"
          disabled={!from && !to}
          onClick={() => {
            setFrom('');
            setTo('');
          }}
        >
          This club year
        </button>
      </div>

      {q.loading ? (
        <Loading rows={4} />
      ) : !d ? null : spent === 0 ? (
        <Empty>Nothing charged to this account between {date(d.from)} and {date(d.to)}.</Empty>
      ) : (
        <>
          <div className="muted small" style={{ marginBottom: 12 }}>
            {date(d.from)} – {date(d.to)} · <strong>{money(d.total_minor, currency)}</strong> used
          </div>

          <div className="grid-2">
            <div>
              <div className="field-label">Where it was spent</div>
              <BarList
                currency={currency}
                items={d.by_department.map((r) => ({
                  label: r.department,
                  value: Number(r.total_minor),
                  hint: `${r.outlet} · ${r.transactions} transaction${
                    r.transactions === 1 ? '' : 's'
                  }`,
                }))}
              />
            </div>

            <div>
              <div className="field-label">Who spent it</div>
              <table className="data">
                <tbody>
                  {d.by_person.map((p) => (
                    <tr key={p.person_id}>
                      <td>
                        {p.name}
                        <div className="muted small">
                          {p.on_the_membership ? (
                            p.role
                          ) : (
                            // Said plainly. A member seeing a name they
                            // do not recognize on their own account will
                            // ring up about it, and this is the answer.
                            <>a guest arrangement — they signed, it billed here</>
                          )}
                          {p.last_used_on
                            ? ` · last used ${date(p.last_used_on)}`
                            : ' · nothing this period'}
                        </div>
                      </td>
                      <td className="num">
                        {Number(p.total_minor) === 0 ? (
                          <span className="muted">—</span>
                        ) : (
                          money(p.total_minor, currency)
                        )}
                      </td>
                    </tr>
                  ))}
                  {Number(d.unattributed.total_minor) !== 0 && (
                    <tr>
                      <td>
                        <span className="muted">Not attributed to a person</span>
                        <div className="muted small">
                          dues, an assessment, or something the office posted directly
                        </div>
                      </td>
                      <td className="num muted">
                        {money(d.unattributed.total_minor, currency)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="hint" style={{ marginTop: 12 }}>
            What the household used. Payments are not on it — this is consumption rather than
            settlement, and the statement is where money in and money out appear together.
          </div>
        </>
      )}
    </Card>
  );
}
